import { type ChangeEvent, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SpinnerInline } from '@/components/ui/spinner';
import { useSupportLink } from '@/hooks/use-support-link';
import { useLogin } from './use-login';

type FieldProps = {
  label: string;
  type: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  autoComplete: string;
};

const GLASS_INPUT = 'border-white/[0.08] bg-white/[0.05] backdrop-blur-xl';
const RECOVER_TEXT = 'Hola Luz, no recuerdo mi contraseña.';

function LoginField(props: FieldProps): ReactNode {
  return <Input {...props} required className={GLASS_INPUT} />;
}

function RecoverLink(): ReactNode {
  const link = useSupportLink();
  if (!link) return null;
  const href = `${link}${link.includes('?') ? '&' : '?'}text=${encodeURIComponent(RECOVER_TEXT)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block text-center text-xs text-text-tertiary hover:text-text-secondary"
    >
      Recuperar contrase&ntilde;a
    </a>
  );
}

function Fields({ s }: { s: ReturnType<typeof useLogin> }): ReactNode {
  return (
    <>
      <LoginField
        label="Email"
        type="email"
        value={s.email}
        onChange={(e) => s.setEmail(e.target.value)}
        autoComplete="email"
      />
      <LoginField
        label="Contraseña"
        type="password"
        value={s.password}
        onChange={(e) => s.setPassword(e.target.value)}
        autoComplete="current-password"
      />
    </>
  );
}

export function LoginForm(): ReactNode {
  const s = useLogin();
  return (
    <form onSubmit={s.handleSubmit} className="space-y-md">
      <Fields s={s} />
      {s.error && <p className="text-xs text-error">{s.error}</p>}
      <Button type="submit" disabled={s.loading} className="mt-sm w-full">
        {s.loading ? <SpinnerInline /> : 'Entrar'}
      </Button>
      <RecoverLink />
    </form>
  );
}
